import { BrowserRouter, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { App as AntdApp, ConfigProvider, Modal, theme as antdTheme } from 'antd';
import zhCN from 'antd-original/locale/zh_CN';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { ErrorBoundary } from './components/ErrorBoundary';
import PermissionRoute from './components/modules/system/Permission/PermissionRoute';
import { SystemRouteGuard } from './security/routeGuards';
import { getCurrentUser } from './services/modules/system/auth';
import { clearUserPermissions } from './services/modules/system/permissionService';
import type { Result as ApiResult } from './services/types';
import { useCRMStore } from './store/crmStore';
import { usePermissionStore } from './store/permissionStore';
import { useUiPreferenceStore } from './store/uiPreferenceStore';
import { EMPTY_USER, type User } from './types';
import { unwrapResultData } from './utils/axiosResult';
import { hasAuthToken } from './utils/authToken';
import { setLogoutCallback } from './utils/authEventManager';
import { modalFormBodyScrollStyles } from './utils/antdModalLayout';
import { WORKBENCH_ACTION_QUEUE_META } from './config/workbenchNavigation';
import { setMessageApi } from './utils/message';
import { useGlobalDraggableModal } from './hooks/useGlobalDraggableModal';
import './index.css';

dayjs.locale('zh-cn');
dayjs.extend(relativeTime);

const MODAL_FORCE_CLOSE_ATTR = 'data-codex-force-close';
const MODAL_BASELINE_ATTR = 'data-codex-modal-baseline';
const MODAL_BASELINE_READY_ATTR = 'data-codex-modal-baseline-ready';

const ProtectedAppLayout = lazy(() => import('./components/app/ProtectedAppLayout'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const WorkbenchDrilldownPage = lazy(() => import('./pages/dashboard/WorkbenchDrilldownPage'));
const WorkbenchQueuePage = lazy(() => import('./pages/dashboard/WorkbenchQueuePage'));
const Companies = lazy(() => import('./pages/modules/crm/Companies'));
const CompanyDetail = lazy(() => import('./pages/modules/crm/CompanyDetail'));
const ContactList = lazy(() => import('./pages/modules/crm/ContactList'));
const ContactDetail = lazy(() => import('./pages/modules/crm/ContactDetail'));
const Leads = lazy(() => import('./pages/modules/crm/Leads'));
const Projects = lazy(() => import('./pages/modules/project/Projects'));
const ProjectDetail = lazy(() => import('./components/modules/project/ProjectDetail'));
const StaffAllocation = lazy(() => import('./pages/modules/project/StaffAllocation'));
const Opportunities = lazy(() => import('./pages/modules/crm/Opportunities'));
const OpportunityDetail = lazy(() => import('./pages/modules/crm/OpportunityDetail'));
const Activities = lazy(() => import('./pages/modules/crm/Activities'));
const Tasks = lazy(() => import('./pages/modules/project/Tasks'));
const Schedule = lazy(() => import('./pages/modules/project/Schedule'));
const Analytics = lazy(() => import('./pages/modules/system/BusinessCockpit'));
const Settings = lazy(() => import('./pages/modules/system/Settings'));
const HrWorkspace = lazy(() => import('./pages/modules/hr/HrWorkspace'));
const HrEmployeeDeepLink = lazy(() => import('./pages/modules/hr/HrEmployeeDeepLink'));
const Experts = lazy(() => import('./pages/modules/expert/Experts'));
const Contracts = lazy(() => import('./pages/modules/finance/Finance/Contracts'));
const Receivables = lazy(() => import('./pages/modules/finance/Finance/Receivables'));
const Invoices = lazy(() => import('./pages/modules/finance/Finance/Invoices'));
const ActivityFees = lazy(() => import('./pages/modules/finance/Finance/ActivityFees'));
const Loans = lazy(() => import('./pages/modules/finance/Finance/Loans'));
const Payables = lazy(() => import('./pages/modules/finance/Finance/Payables'));
const Suppliers = lazy(() => import('./pages/modules/finance/Finance/Suppliers'));
const SupplierDetail = lazy(() => import('./pages/modules/finance/Finance/SupplierDetail'));
const Expenses = lazy(() => import('./pages/modules/finance/Finance/Expenses'));
const MonthlyExpenses = lazy(() => import('./pages/modules/finance/Finance/MonthlyExpenses'));
const ProjectFinances = lazy(() => import('./pages/modules/finance/Finance/ProjectFinances'));
const FinanceReports = lazy(() => import('./pages/modules/finance/Finance/FinanceReports'));
const KnowledgeLibrary = lazy(() => import('./pages/modules/knowledge/Knowledge'));
const HelpCenter = lazy(() => import('./pages/help'));
const MarketingAssistant = lazy(() => import('./pages/modules/marketing/Marketing'));
const UserManagement = lazy(() => import('./pages/modules/system/System/UserManagement'));
const RoleManagement = lazy(() => import('./pages/modules/system/System/RoleManagement'));
const DictManagement = lazy(() => import('./pages/modules/system/System/DictManagement'));
const DepartmentManagement = lazy(() => import('./pages/modules/system/System/DepartmentManagement'));
const OperationLog = lazy(() => import('./pages/modules/system/System/OperationLog'));
const PermissionManagement = lazy(() => import('./pages/modules/system/System/PermissionManagement'));
const MenuManagement = lazy(() => import('./pages/modules/system/System/MenuManagement'));
const DataPermissionManagement = lazy(() => import('./pages/modules/system/System/DataPermissionManagement'));
const Diagnosis = lazy(() => import('./pages/modules/system/Diagnosis'));
const LicenseManagement = lazy(() => import('./pages/modules/system/System/LicenseManagement'));
const GaoqianBilling = lazy(() => import('./extension/gaoqian/billing/BillingManagement'));
const SecurityAudit = lazy(() => import('./pages/modules/security/SecurityAudit'));
const MaskStrategy = lazy(() => import('./pages/modules/security/MaskStrategy'));
const WorkflowDetail = lazy(() => import('./pages/modules/system/Workflow/Detail'));
const WorkflowDesigner = lazy(() => import('./pages/modules/system/Workflow/Designer'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Login = lazy(() => import('./pages/Login'));
const PublicActivityRegister = lazy(() => import('./pages/public/PublicActivityRegister'));
const PublicActivityCheckIn = lazy(() => import('./pages/public/PublicActivityCheckIn'));

function serializeModalControls(modalEl: HTMLElement): string {
  const nodes = modalEl.querySelectorAll<HTMLElement>(
    'input, textarea, select, .ant-select-selector, .ant-picker-input input, .ant-input-number-input',
  );

  const values = Array.from(nodes).map((node, index) => {
    if (node instanceof HTMLInputElement) {
      if (node.type === 'checkbox' || node.type === 'radio') {
        return `${index}:${node.type}:${node.checked ? '1' : '0'}`;
      }
      return `${index}:input:${node.value}`;
    }
    if (node instanceof HTMLTextAreaElement) {
      return `${index}:textarea:${node.value}`;
    }
    if (node instanceof HTMLSelectElement) {
      const value = node.multiple
        ? Array.from(node.selectedOptions).map((option) => option.value).join('|')
        : node.value;
      return `${index}:select:${value}`;
    }
    return `${index}:node:${node.textContent?.trim() || ''}`;
  });

  return JSON.stringify(values);
}

function scheduleModalBaseline(modalEl: HTMLElement, timers: WeakMap<HTMLElement, number>) {
  const existing = timers.get(modalEl);
  if (existing) {
    window.clearTimeout(existing);
  }
  const timer = window.setTimeout(() => {
    modalEl.setAttribute(MODAL_BASELINE_ATTR, serializeModalControls(modalEl));
    modalEl.setAttribute(MODAL_BASELINE_READY_ATTR, 'true');
    timers.delete(modalEl);
  }, 260);
  timers.set(modalEl, timer);
}

function isCancelLikeButton(button: HTMLElement): boolean {
  if (!button.classList.contains('ant-btn')) return false;
  if (!button.classList.contains('ant-btn-primary')) {
    const text = button.textContent?.trim() || '';
    return /^(取消|关闭|返回)$/.test(text);
  }
  return false;
}

function useGlobalSafeModalClose() {
  useEffect(() => {
    const baselineTimers = new WeakMap<HTMLElement, number>();

    const refreshBaselines = () => {
      document.querySelectorAll<HTMLElement>('.ant-modal').forEach((modalEl) => {
        if (modalEl.offsetParent === null) return;
        if (modalEl.getAttribute(MODAL_BASELINE_READY_ATTR) === 'true') return;
        scheduleModalBaseline(modalEl, baselineTimers);
      });
    };

    const observer = new MutationObserver(() => {
      refreshBaselines();
    });

    refreshBaselines();
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });

    const handleClickCapture = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;

      const trigger = target.closest<HTMLElement>('.ant-modal-close, .ant-modal-footer .ant-btn');
      if (!trigger) return;
      if (!trigger.classList.contains('ant-modal-close') && !isCancelLikeButton(trigger)) return;

      const modalEl = target.closest<HTMLElement>('.ant-modal');
      if (!modalEl) return;
      if (modalEl.getAttribute(MODAL_FORCE_CLOSE_ATTR) === 'true') return;
      if (modalEl.getAttribute(MODAL_BASELINE_READY_ATTR) !== 'true') return;

      const baseline = modalEl.getAttribute(MODAL_BASELINE_ATTR) || '';
      const current = serializeModalControls(modalEl);
      if (!baseline || baseline === current) return;

      event.preventDefault();
      event.stopPropagation();

      Modal.confirm({
        title: '放弃本次编辑？',
        content: '当前弹窗里还有未保存的内容，关闭后这些修改会丢失。',
        okText: '放弃并关闭',
        cancelText: '继续编辑',
        maskClosable: false,
        keyboard: false,
        onOk: () => {
          modalEl.setAttribute(MODAL_FORCE_CLOSE_ATTR, 'true');
          window.setTimeout(() => {
            trigger.click();
            window.setTimeout(() => {
              modalEl.removeAttribute(MODAL_FORCE_CLOSE_ATTR);
              modalEl.removeAttribute(MODAL_BASELINE_READY_ATTR);
            }, 0);
          }, 0);
        },
      });
    };

    document.addEventListener('click', handleClickCapture, true);

    return () => {
      document.removeEventListener('click', handleClickCapture, true);
      observer.disconnect();
    };
  }, []);
}

function GlobalModalEnhancements() {
  useGlobalDraggableModal();
  useGlobalSafeModalClose();
  return null;
}

const PageLoader = () => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '400px',
      gap: '12px',
    }}
  >
    <div
      style={{
        width: 36,
        height: 36,
        borderRadius: '50%',
        border: '3px solid rgba(22, 119, 255, 0.16)',
        borderTopColor: '#1677ff',
        animation: 'codex-app-spin 0.9s linear infinite',
      }}
    />
    <div style={{ color: '#64748b', fontSize: 14 }}>正在加载...</div>
  </div>
);

function sanitizeUser(userData: User): User {
  const cloned = { ...userData } as Record<string, unknown>;
  delete cloned.password;
  return cloned as unknown as User;
}

const useAuth = () => {
  const setCurrentUser = useCRMStore((state) => state.setCurrentUser);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [user, setUser] = useState<User | null>(null);

  const silentResetSessionState = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('nexus_crm_auth');
    usePermissionStore.getState().clearPermissions();
    setCurrentUser(EMPTY_USER);
    setUser(null);
    setIsAuthenticated(false);
  }, [setCurrentUser]);

  const logout = useCallback(() => {
    silentResetSessionState();
    clearUserPermissions();
  }, [silentResetSessionState]);

  const validateSession = useCallback(async (): Promise<boolean> => {
    const token = localStorage.getItem('token');
    if (!token) {
      silentResetSessionState();
      return false;
    }

    try {
      const raw = (await getCurrentUser()) as ApiResult<User>;
      const remoteUser = unwrapResultData<User>(raw);
      if (!remoteUser?.id) {
        logout();
        return false;
      }

      const safeUser = sanitizeUser(remoteUser);
      localStorage.setItem('user', JSON.stringify(safeUser));
      setCurrentUser(safeUser);
      setUser(safeUser);
      setIsAuthenticated(true);
      return true;
    } catch {
      logout();
      return false;
    }
  }, [logout, silentResetSessionState, setCurrentUser]);

  const login = useCallback(
    async (token?: string, userData?: User): Promise<boolean> => {
      if (token) {
        localStorage.setItem('token', token);
      }
      if (userData) {
        const safeUser = sanitizeUser(userData);
        localStorage.setItem('user', JSON.stringify(safeUser));
        setCurrentUser(safeUser);
        setUser(safeUser);
      }
      if (token && userData?.id) {
        setIsAuthenticated(true);
        return true;
      }
      return validateSession();
    },
    [validateSession, setCurrentUser],
  );

  useEffect(() => {
    let cancelled = false;

    const hydrate = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          silentResetSessionState();
          return;
        }
        await validateSession();
      } catch {
        if (!cancelled) {
          logout();
        }
      } finally {
        if (!cancelled) {
          setIsChecking(false);
        }
      }
    };

    void hydrate();

    return () => {
      cancelled = true;
    };
  }, [logout, validateSession, silentResetSessionState]);

  useEffect(() => {
    if (!isChecking && isAuthenticated && !hasAuthToken()) {
      logout();
    }
  }, [isChecking, isAuthenticated, logout]);

  return { isAuthenticated, isChecking, user, login, logout };
};

interface AppRoutesProps {
  isAuthenticated: boolean;
  isChecking: boolean;
  user: User | null;
  login: (token?: string, userData?: User) => Promise<boolean>;
  logout: () => void;
}

const AppRoutes: React.FC<AppRoutesProps> = ({
  isAuthenticated,
  isChecking,
  user: _user,
  login,
  logout,
}) => {
  const navigate = useNavigate();

  useEffect(() => {
    setLogoutCallback(() => {
      logout();
      navigate('/login');
    });
  }, [logout, navigate]);

  if (isChecking) {
    return <PageLoader />;
  }

  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route
            path="/login"
            element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage onLogin={login} />}
          />
          <Route path="/public/activity/:token/register" element={<PublicActivityRegister />} />
          <Route path="/public/activity/:token/check-in" element={<PublicActivityCheckIn />} />
          <Route
            path="/"
            element={
              isAuthenticated ? <ProtectedAppLayout onLogout={logout} /> : <Navigate to="/login" replace />
            }
          >
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="dashboard/drilldown/:metric" element={<WorkbenchDrilldownPage />} />
            <Route path="dashboard/messages" element={<WorkbenchQueuePage type="messages" />} />
            <Route path="dashboard/todos" element={<Navigate to="/dashboard/approvals" replace />} />
            <Route path="dashboard/approvals" element={<PermissionRoute anyOf={[...WORKBENCH_ACTION_QUEUE_META.approvals.permissions]}><WorkbenchQueuePage type="approvals" /></PermissionRoute>} />
            <Route path="dashboard/task-confirmations" element={<PermissionRoute permission="crm.task.list"><WorkbenchQueuePage type="task-confirmations" /></PermissionRoute>} />
            <Route path="dashboard/schedule-confirmations" element={<PermissionRoute permission="crm.schedule.list"><WorkbenchQueuePage type="schedule-confirmations" /></PermissionRoute>} />
            <Route path="dashboard/expense-allocations" element={<PermissionRoute permission="fin.expense.list"><WorkbenchQueuePage type="expense-allocations" /></PermissionRoute>} />
            <Route path="companies" element={<PermissionRoute permission="crm.company.list"><Companies /></PermissionRoute>} />
            <Route path="companies/:id" element={<PermissionRoute permission="crm.company.get"><CompanyDetail /></PermissionRoute>} />
            <Route path="leads" element={<PermissionRoute permission="crm.lead.list"><Leads /></PermissionRoute>} />
            <Route path="contacts/:id" element={<PermissionRoute permission="crm.contact.get"><ContactDetail /></PermissionRoute>} />
            <Route path="contacts" element={<PermissionRoute permission="crm.contact.list"><ContactList /></PermissionRoute>} />
            <Route path="projects" element={<PermissionRoute permission="crm.project.list"><Projects /></PermissionRoute>} />
            <Route path="projects/:id" element={<PermissionRoute permission="crm.project.get"><ProjectDetail /></PermissionRoute>} />
            <Route path="staff-allocation" element={<PermissionRoute permission="crm.project.list"><StaffAllocation /></PermissionRoute>} />
            <Route path="opportunities" element={<PermissionRoute permission="crm.opportunity.list"><Opportunities /></PermissionRoute>} />
            <Route path="opportunities/:id" element={<PermissionRoute permission="crm.opportunity.get"><OpportunityDetail /></PermissionRoute>} />
            <Route path="activities" element={<PermissionRoute permission="crm.activity.list"><Activities /></PermissionRoute>} />
            <Route path="tasks" element={<PermissionRoute permission="crm.task.list"><Tasks /></PermissionRoute>} />
            <Route path="schedule" element={<PermissionRoute permission="crm.schedule.list"><Schedule /></PermissionRoute>} />
            <Route
              path="analytics"
              element={
                <PermissionRoute permission="sys.analytics.view">
                  <Analytics />
                </PermissionRoute>
              }
            />
            <Route
              path="hr"
              element={
                <SystemRouteGuard routeKey="hr">
                  <PermissionRoute>
                    <HrWorkspace />
                  </PermissionRoute>
                </SystemRouteGuard>
              }
            />
            <Route
              path="hr/employees/:id"
              element={
                <PermissionRoute permission="hr.employee.get">
                  <HrEmployeeDeepLink />
                </PermissionRoute>
              }
            />
            <Route path="experts" element={<PermissionRoute permission="expert.external.list"><Experts /></PermissionRoute>} />
            <Route path="settings" element={<Settings />} />
            <Route path="finance/contracts" element={<PermissionRoute permission="fin.contract.list"><Contracts /></PermissionRoute>} />
            <Route path="finance/receivables" element={<PermissionRoute permission="fin.receivable.list"><Receivables /></PermissionRoute>} />
            <Route path="finance/invoices" element={<PermissionRoute permission="fin.invoice.list"><Invoices /></PermissionRoute>} />
            <Route path="finance/activity-fees" element={<PermissionRoute permission="crm.activity.get"><ActivityFees /></PermissionRoute>} />
            <Route path="finance/loans" element={<PermissionRoute permission="fin.loan.list"><Loans /></PermissionRoute>} />
            <Route path="finance/payables" element={<PermissionRoute permission="fin.payable.list"><Payables /></PermissionRoute>} />
            <Route path="finance/suppliers" element={<PermissionRoute anyOf={['fin.supplier.list', 'crm.supplier.list']}><Suppliers /></PermissionRoute>} />
            <Route path="finance/suppliers/:id" element={<PermissionRoute anyOf={['fin.supplier.get', 'crm.supplier.get']}><SupplierDetail /></PermissionRoute>} />
            <Route path="finance/expenses" element={<PermissionRoute permission="fin.expense.list"><Expenses /></PermissionRoute>} />
            <Route path="finance/monthly-expenses" element={<PermissionRoute permission="fin.expense.list"><MonthlyExpenses /></PermissionRoute>} />
            <Route path="finance/project-finances" element={<PermissionRoute permission="fin.fund.list"><ProjectFinances /></PermissionRoute>} />
            <Route path="finance/reports" element={<PermissionRoute permission="sys.analytics.view"><FinanceReports /></PermissionRoute>} />
            <Route path="knowledge" element={<PermissionRoute permission="kb.article.list"><KnowledgeLibrary /></PermissionRoute>} />
            <Route path="help" element={<HelpCenter />} />
            <Route path="marketing" element={<PermissionRoute permission="crm.company.list"><MarketingAssistant /></PermissionRoute>} />
            <Route path="system/users" element={<PermissionRoute permission="sys.user.list"><UserManagement /></PermissionRoute>} />
            <Route path="system/roles" element={<PermissionRoute permission="sys.role.list"><RoleManagement /></PermissionRoute>} />
            <Route path="system/dicts" element={<PermissionRoute permission="sys.dict.list"><DictManagement /></PermissionRoute>} />
            <Route path="system/departments" element={<PermissionRoute permission="sys.department.list"><DepartmentManagement /></PermissionRoute>} />
            <Route path="system/logs" element={<PermissionRoute permission="sys.log.list"><OperationLog /></PermissionRoute>} />
            <Route path="system/permissions" element={<PermissionRoute permission="sys.permission.list"><PermissionManagement /></PermissionRoute>} />
            <Route path="system/menus" element={<PermissionRoute permission="sys.menu.list"><MenuManagement /></PermissionRoute>} />
            <Route path="system/data-permissions" element={<PermissionRoute permission="sys.datapermission.list"><DataPermissionManagement /></PermissionRoute>} />
            <Route path="system/diagnosis" element={<PermissionRoute permission="sys.diagnosis.view"><Diagnosis /></PermissionRoute>} />
            <Route path="system/license" element={<PermissionRoute permission="sys.license.view"><LicenseManagement /></PermissionRoute>} />
            <Route path="security/audit" element={<PermissionRoute anyOf={['security:audit:list', 'security:audit:detail']}><SecurityAudit /></PermissionRoute>} />
            <Route path="security/mask-strategy" element={<PermissionRoute permission="mask:strategy:view"><MaskStrategy /></PermissionRoute>} />
            <Route path="workflow" element={<Navigate to="/dashboard/approvals" replace />} />
            <Route path="workflow/detail/:id" element={<PermissionRoute anyOf={['workflow.flow.get', 'workflow.flow.approve']}><WorkflowDetail /></PermissionRoute>} />
            <Route path="workflow/designer" element={<PermissionRoute permission="workflow.flow.add"><WorkflowDesigner /></PermissionRoute>} />
            <Route
              path="extension/gaoqian/billing"
              element={
                <PermissionRoute permission="gq.billing.view">
                  <GaoqianBilling />
                </PermissionRoute>
              }
            />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
};

const MessageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { message } = AntdApp.useApp();

  useEffect(() => {
    setMessageApi(message);
  }, [message]);

  return <>{children}</>;
};

const LoginPage: React.FC<{ onLogin: (token?: string, userData?: User) => Promise<boolean> }> = ({ onLogin }) => {
  return <Login onLogin={onLogin} />;
};

function App() {
  const { isAuthenticated, isChecking, user, login, logout } = useAuth();
  const darkMode = useUiPreferenceStore((s) => s.darkMode);

  useEffect(() => {
    useUiPreferenceStore.getState().hydrateFromLocal();
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  return (
    <ConfigProvider
      locale={zhCN}
      modal={{
        styles: modalFormBodyScrollStyles,
      }}
      theme={{
        algorithm: darkMode ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
        token: {
          colorPrimary: '#1677ff',
          colorText: '#1f2328',
          colorTextSecondary: '#475467',
          colorTextTertiary: '#667085',
          colorTextQuaternary: '#98a2b3',
          borderRadius: 8,
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        },
      }}
    >
      <AntdApp>
        <GlobalModalEnhancements />
        <MessageProvider>
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <AppRoutes
              isAuthenticated={isAuthenticated}
              isChecking={isChecking}
              user={user}
              login={login}
              logout={logout}
            />
          </BrowserRouter>
        </MessageProvider>
      </AntdApp>
    </ConfigProvider>
  );
}

export default App;
